import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  AlertTriangle, Check, CheckSquare, Gift, Loader2, Mail, Printer, RefreshCw,
  Search, Square, Ticket, X,
} from 'lucide-react';
import toast from 'react-hot-toast';
import clsx from 'clsx';
import {
  chargerBons, chargerPeriodes, editerLot, envoyerBon, planchePdf, telecharger,
  LIBELLE_ETAT, ORIGINES,
  type BonCadeau, type EtatDuBon, type Periode,
} from '../../lib/gratuity/gateway.supabase';
import { messageErreur } from '../../lib/payments/erreur';

/*
  Les bons cadeaux : les éditer, les imprimer, les envoyer.

  ## Le coupon est fabriqué ICI, dans le navigateur

  Ni fonction edge, ni bucket. Un bon est un instrument au porteur : une URL
  qui fuit vaut une semaine de stage gratuite. Le PDF est dessiné sur ce poste,
  à partir de codes que l'administration a déjà le droit de lire, et il ne se
  dépose nulle part. L'impression est immédiate, et il n'y a rien à nettoyer.

  L'envoi par courriel a besoin d'un serveur SMTP : le navigateur fabrique le
  PDF et le confie à `send-gratuity-email`, qui ne fait que l'envoyer — depuis
  stage-notif@eclosion.be.

  ## Ce que l'écran ne propose pas, et pourquoi

  Pas de suppression. Un bon distribué existe sur du papier : le retirer de la
  base ne le retire pas du cartable, et un parent se présenterait avec un code
  « qui n'existe pas ». Un bon dont on ne veut plus se périme.
*/

const PAR_PAGE = 50;

const AdminGiftCodesPage = () => {
  const [bons, setBons] = useState<BonCadeau[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [chargement, setChargement] = useState(true);
  const [filtre, setFiltre] = useState<EtatDuBon | 'tous'>('tous');
  const [recherche, setRecherche] = useState('');
  const [choisis, setChoisis] = useState<Set<string>>(new Set());
  const [periodes, setPeriodes] = useState<Periode[]>([]);
  const [enCours, setEnCours] = useState(false);
  const [modaleLot, setModaleLot] = useState(false);
  const [envoiPour, setEnvoiPour] = useState<BonCadeau | null>(null);

  const recharger = useCallback(async () => {
    setChargement(true);
    try {
      const r = await chargerBons({ etat: filtre, recherche, page, parPage: PAR_PAGE });
      setBons(r.bons);
      setTotal(r.total);
    } catch (e) {
      toast.error(messageErreur(e, 'Lecture des bons impossible'));
    } finally {
      setChargement(false);
    }
  }, [filtre, recherche, page]);

  useEffect(() => { void recharger(); }, [recharger]);
  useEffect(() => {
    chargerPeriodes().then(setPeriodes).catch(() => setPeriodes([]));
  }, []);

  /* Un changement de filtre ramène à la première page : rester en page 4 d'une
     liste qui n'en compte plus qu'une afficherait un tableau vide. */
  useEffect(() => { setPage(0); }, [filtre, recherche]);

  const basculer = (id: string) => {
    const s = new Set(choisis);
    if (s.has(id)) s.delete(id); else s.add(id);
    setChoisis(s);
  };

  const bonsChoisis = useMemo(
    () => bons.filter((b) => choisis.has(b.id)),
    [bons, choisis],
  );

  const imprimer = async (aImprimer: BonCadeau[]) => {
    if (aImprimer.length === 0) return;
    setEnCours(true);
    try {
      const pdf = await planchePdf(aImprimer);
      telecharger(pdf, `bons-cadeaux-${new Date().toISOString().slice(0, 10)}.pdf`);
      toast.success(
        aImprimer.length === 1
          ? 'Coupon prêt à imprimer'
          : `${aImprimer.length} coupons sur ${Math.ceil(aImprimer.length / 4)} page(s)`,
      );
    } catch (e) {
      toast.error(messageErreur(e, 'Le coupon n\'a pas pu être produit'));
    } finally {
      setEnCours(false);
    }
  };

  return (
    <div>
      <div className="mb-6 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-bold text-gray-900">
            <Gift className="h-6 w-6 text-teal-600" />
            Bons cadeaux
          </h1>
          <p className="mt-1 text-gray-600">
            Une semaine de plaine offerte. Le bon s'utilise une seule fois, sur la semaine
            la moins chère du panier.
          </p>
        </div>
        <button onClick={() => setModaleLot(true)} className="btn-primary flex items-center gap-2">
          <Ticket className="h-4 w-4" /> Éditer des bons
        </button>
      </div>

      {/* --- Filtres --- */}
      <div className="mb-4 flex flex-wrap items-center gap-2">
        {(['tous', 'disponible', 'consomme', 'a_venir', 'perime'] as const).map((e) => (
          <button
            key={e}
            onClick={() => setFiltre(e)}
            className={clsx(
              'rounded-full px-3 py-1.5 text-sm font-medium',
              filtre === e ? 'bg-teal-600 text-white' : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50',
            )}
          >
            {e === 'tous' ? 'Tous' : LIBELLE_ETAT[e]}
          </button>
        ))}

        <div className="relative ml-auto">
          <Search className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-gray-400" />
          <input
            value={recherche}
            onChange={(ev) => setRecherche(ev.target.value)}
            placeholder="Code ou libellé"
            className="w-56 rounded-md border border-gray-300 py-2 pl-8 pr-3 text-sm"
          />
        </div>
        <button
          onClick={() => void recharger()}
          className="rounded-md border border-gray-300 bg-white p-2 text-gray-600 hover:bg-gray-50"
          aria-label="Rafraîchir"
        >
          <RefreshCw className={clsx('h-4 w-4', chargement && 'animate-spin')} />
        </button>
      </div>

      {/* --- Barre de sélection --- */}
      {choisis.size > 0 && (
        <div className="mb-3 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-teal-200 bg-teal-50 p-3">
          <span className="text-sm font-medium text-teal-900">
            {choisis.size} bon{choisis.size > 1 ? 's' : ''} sélectionné{choisis.size > 1 ? 's' : ''}
            {' · '}
            {Math.ceil(bonsChoisis.length / 4)} page{Math.ceil(bonsChoisis.length / 4) > 1 ? 's' : ''} à imprimer
          </span>
          <div className="flex gap-2">
            <button
              disabled={enCours}
              onClick={() => void imprimer(bonsChoisis)}
              className="btn-primary flex items-center gap-2 disabled:opacity-50"
            >
              {enCours ? <Loader2 className="h-4 w-4 animate-spin" /> : <Printer className="h-4 w-4" />}
              Imprimer la planche
            </button>
            <button onClick={() => setChoisis(new Set())} className="btn-outline flex items-center gap-1.5">
              <X className="h-4 w-4" /> Désélectionner
            </button>
          </div>
        </div>
      )}

      {/* --- Le tableau --- */}
      <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="w-10 px-3 py-3">
                <button
                  onClick={() => setChoisis(
                    choisis.size === bons.length ? new Set() : new Set(bons.map((b) => b.id)),
                  )}
                  className="text-gray-500 hover:text-gray-700"
                  aria-label="Tout sélectionner"
                >
                  {choisis.size === bons.length && bons.length > 0
                    ? <CheckSquare className="h-4 w-4" /> : <Square className="h-4 w-4" />}
                </button>
              </th>
              <th className="px-3 py-3 text-left text-xs font-medium uppercase text-gray-500">Code</th>
              <th className="px-3 py-3 text-left text-xs font-medium uppercase text-gray-500">Lot</th>
              <th className="px-3 py-3 text-left text-xs font-medium uppercase text-gray-500">Périodes</th>
              <th className="px-3 py-3 text-left text-xs font-medium uppercase text-gray-500">Validité</th>
              <th className="px-3 py-3 text-left text-xs font-medium uppercase text-gray-500">État</th>
              <th className="px-3 py-3 text-right text-xs font-medium uppercase text-gray-500">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {chargement && bons.length === 0 && (
              <tr><td colSpan={7} className="px-3 py-10 text-center">
                <Loader2 className="mx-auto h-6 w-6 animate-spin text-teal-600" />
              </td></tr>
            )}
            {!chargement && bons.length === 0 && (
              <tr><td colSpan={7} className="px-3 py-10 text-center text-gray-500">
                Aucun bon ne correspond. Utilisez « Éditer des bons » pour en créer.
              </td></tr>
            )}
            {bons.map((b) => (
              <tr key={b.id} className={clsx(choisis.has(b.id) && 'bg-teal-50/50')}>
                <td className="px-3 py-2.5">
                  <button onClick={() => basculer(b.id)} className="text-gray-500 hover:text-teal-600">
                    {choisis.has(b.id) ? <CheckSquare className="h-4 w-4" /> : <Square className="h-4 w-4" />}
                  </button>
                </td>
                <td className="px-3 py-2.5 font-mono text-sm font-semibold tracking-wider text-gray-900">
                  {b.code}
                </td>
                <td className="px-3 py-2.5 text-sm text-gray-600">
                  {b.label ?? <span className="text-gray-400">—</span>}
                  {b.issuedToName && (
                    <span className="mt-0.5 block text-xs text-gray-500">
                      Nominatif : {b.issuedToName}
                    </span>
                  )}
                </td>
                <td className="px-3 py-2.5 text-sm text-gray-600">
                  {b.periodLabels.length === 0
                    ? <span className="text-gray-500">Toutes</span>
                    : b.periodLabels.join(', ')}
                </td>
                <td className="px-3 py-2.5 text-xs text-gray-600">
                  {b.validFrom && <>du {fr(b.validFrom)}<br /></>}
                  {b.expiresAt ? `au ${fr(b.expiresAt)}` : 'sans échéance'}
                </td>
                <td className="px-3 py-2.5">
                  <span className={clsx(
                    'inline-flex items-center rounded px-2 py-0.5 text-xs font-medium',
                    b.etat === 'disponible' && 'bg-green-100 text-green-800',
                    b.etat === 'consomme' && 'bg-gray-200 text-gray-700',
                    b.etat === 'perime' && 'bg-red-100 text-red-800',
                    b.etat === 'a_venir' && 'bg-amber-100 text-amber-800',
                  )}>
                    {LIBELLE_ETAT[b.etat]}
                  </span>
                  {b.etat === 'consomme' && b.redeemedByName && (
                    <span className="mt-0.5 block text-xs text-gray-500">
                      par {b.redeemedByName}
                    </span>
                  )}
                </td>
                <td className="px-3 py-2.5 text-right">
                  <div className="flex justify-end gap-1">
                    <button
                      disabled={enCours}
                      onClick={() => void imprimer([b])}
                      className="rounded border border-gray-300 bg-white p-1.5 text-gray-600 hover:bg-gray-50 disabled:opacity-50"
                      title="Imprimer ce coupon"
                    >
                      <Printer className="h-3.5 w-3.5" />
                    </button>
                    <button
                      disabled={b.etat === 'consomme' || b.etat === 'perime'}
                      onClick={() => setEnvoiPour(b)}
                      className="rounded border border-gray-300 bg-white p-1.5 text-gray-600 hover:bg-gray-50 disabled:opacity-40"
                      title={b.etat === 'consomme' ? 'Bon déjà utilisé'
                        : b.etat === 'perime' ? 'Bon périmé' : 'Envoyer par email'}
                    >
                      <Mail className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {total > PAR_PAGE && (
          <div className="flex items-center justify-between border-t border-gray-200 px-3 py-2 text-sm text-gray-600">
            <span>
              {page * PAR_PAGE + 1}–{Math.min((page + 1) * PAR_PAGE, total)} sur {total}
            </span>
            <div className="flex gap-2">
              <button
                disabled={page === 0}
                onClick={() => setPage(page - 1)}
                className="rounded border border-gray-300 px-2 py-1 disabled:opacity-40"
              >Précédent</button>
              <button
                disabled={(page + 1) * PAR_PAGE >= total}
                onClick={() => setPage(page + 1)}
                className="rounded border border-gray-300 px-2 py-1 disabled:opacity-40"
              >Suivant</button>
            </div>
          </div>
        )}
      </div>

      {modaleLot && (
        <ModaleLot
          periodes={periodes}
          onFerme={() => setModaleLot(false)}
          onCree={async (crees) => {
            setModaleLot(false);
            await recharger();
            /* Les nouveaux bons sont pré-sélectionnés : on les édite pour les imprimer. */
            setChoisis(new Set(crees.map((c) => c.id)));
            toast.success(`${crees.length} bon${crees.length > 1 ? 's' : ''} édité${crees.length > 1 ? 's' : ''}`);
          }}
        />
      )}

      {envoiPour && (
        <ModaleEnvoi
          bon={envoiPour}
          onFerme={() => setEnvoiPour(null)}
          onEnvoye={() => { setEnvoiPour(null); void recharger(); }}
        />
      )}
    </div>
  );
};

function fr(iso: string): string {
  const [a, m, j] = iso.slice(0, 10).split('-');
  return `${j}/${m}/${a}`;
}

/* -------------------------------------------------------------------------- */

function ModaleLot({
  periodes, onFerme, onCree,
}: {
  periodes: Periode[];
  onFerme: () => void;
  onCree: (crees: { id: string; code: string }[]) => void;
}) {
  const dansUnAn = new Date();
  dansUnAn.setFullYear(dansUnAn.getFullYear() + 1);

  const [nombre, setNombre] = useState(10);
  const [origine, setOrigine] = useState('tombola');
  const [libelle, setLibelle] = useState('');
  const [du, setDu] = useState(new Date().toISOString().slice(0, 10));
  const [au, setAu] = useState(dansUnAn.toISOString().slice(0, 10));
  const [choisies, setChoisies] = useState<Set<string>>(new Set());
  const [notes, setNotes] = useState('');
  const [enCours, setEnCours] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);

  const soumettre = async () => {
    setEnCours(true);
    setErreur(null);
    try {
      const crees = await editerLot({
        nombre,
        origine,
        libelle: libelle.trim() || null,
        valableDu: du || null,
        valableAu: au || null,
        periodIds: [...choisies],
        issuedToUserId: null,
        notes: notes.trim() || null,
      });
      onCree(crees);
    } catch (e) {
      setErreur(messageErreur(e, "Le lot n'a pas pu être édité"));
    } finally {
      setEnCours(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4">
      <div className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-xl bg-white p-6 shadow-xl">
        <h2 className="mb-1 text-lg font-semibold">Éditer des bons cadeaux</h2>
        <p className="mb-5 text-sm text-gray-600">
          Le lot est créé d'un bloc : tous les bons, ou aucun.
        </p>

        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="text-sm font-medium text-gray-700">Nombre de bons</span>
              <input
                type="number" min={1} max={500} value={nombre}
                onChange={(e) => setNombre(Math.max(1, Math.min(500, Number(e.target.value) || 1)))}
                className="mt-1 w-full rounded-md border-gray-300 text-sm"
              />
            </label>
            <label className="block">
              <span className="text-sm font-medium text-gray-700">Origine</span>
              <select
                value={origine} onChange={(e) => setOrigine(e.target.value)}
                className="mt-1 w-full rounded-md border-gray-300 text-sm"
              >
                {ORIGINES.map((o) => <option key={o.valeur} value={o.valeur}>{o.libelle}</option>)}
              </select>
            </label>
          </div>

          <label className="block">
            <span className="text-sm font-medium text-gray-700">Libellé du lot</span>
            <input
              value={libelle} onChange={(e) => setLibelle(e.target.value)}
              placeholder="Ex. Tombola école des Cèdres 2026"
              className="mt-1 w-full rounded-md border-gray-300 text-sm"
            />
            <span className="mt-1 block text-xs text-gray-500">
              Pour vous retrouver plus tard. Il n'apparaît pas sur le coupon.
            </span>
          </label>

          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="text-sm font-medium text-gray-700">Valable à partir du</span>
              <input type="date" value={du} onChange={(e) => setDu(e.target.value)}
                className="mt-1 w-full rounded-md border-gray-300 text-sm" />
            </label>
            <label className="block">
              <span className="text-sm font-medium text-gray-700">Jusqu'au</span>
              <input type="date" value={au} onChange={(e) => setAu(e.target.value)}
                className="mt-1 w-full rounded-md border-gray-300 text-sm" />
            </label>
          </div>

          <div>
            <span className="text-sm font-medium text-gray-700">Saisons couvertes</span>
            <p className="mb-2 text-xs text-gray-500">
              Aucune cochée : le bon vaut sur toutes les saisons. C'est cette liste qui est
              imprimée sur le coupon, et c'est elle que le système fait respecter. L'année
              ne se choisit pas ici : c'est la date de validité ci-dessus qui la borne.
            </p>
            <div className="max-h-40 space-y-1 overflow-y-auto rounded-md border border-gray-200 p-2">
              {periodes.length === 0 && (
                <p className="p-2 text-sm text-gray-500">Aucune période enregistrée.</p>
              )}
              {periodes.map((p) => (
                <label key={p.id} className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={choisies.has(p.id)}
                    onChange={() => {
                      const s = new Set(choisies);
                      if (s.has(p.id)) s.delete(p.id); else s.add(p.id);
                      setChoisies(s);
                    }}
                    className="h-4 w-4 rounded border-gray-300 text-teal-600"
                  />
                  {p.label}
                </label>
              ))}
            </div>
          </div>

          <label className="block">
            <span className="text-sm font-medium text-gray-700">Note interne</span>
            <input
              value={notes} onChange={(e) => setNotes(e.target.value)}
              className="mt-1 w-full rounded-md border-gray-300 text-sm"
            />
          </label>

          {erreur && (
            <div className="flex items-start gap-2 rounded-md border border-red-200 bg-red-50 p-3">
              <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0 text-red-600" />
              <p className="text-sm text-red-700">{erreur}</p>
            </div>
          )}
        </div>

        <div className="mt-6 flex justify-end gap-3">
          <button onClick={onFerme} className="btn-outline">Annuler</button>
          <button
            onClick={() => void soumettre()}
            disabled={enCours}
            className="btn-primary flex items-center gap-2 disabled:opacity-50"
          >
            {enCours ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
            Éditer {nombre} bon{nombre > 1 ? 's' : ''}
          </button>
        </div>
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */

function ModaleEnvoi({
  bon, onFerme, onEnvoye,
}: {
  bon: BonCadeau;
  onFerme: () => void;
  onEnvoye: () => void;
}) {
  const [adresse, setAdresse] = useState(bon.issuedToEmail ?? '');
  const [mot, setMot] = useState('');
  const [enCours, setEnCours] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);

  const envoyer = async () => {
    setEnCours(true);
    setErreur(null);
    try {
      await envoyerBon(bon, adresse, mot || null);
      toast.success(`Bon ${bon.code} envoyé à ${adresse}`);
      onEnvoye();
    } catch (e) {
      setErreur(messageErreur(e, "L'envoi a échoué"));
    } finally {
      setEnCours(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4">
      <div className="w-full max-w-md rounded-xl bg-white p-6 shadow-xl">
        <h2 className="mb-1 flex items-center gap-2 text-lg font-semibold">
          <Mail className="h-5 w-5 text-teal-600" />
          Envoyer le bon {bon.code}
        </h2>
        <p className="mb-5 text-sm text-gray-600">
          Un seul bon par courriel, avec son coupon en pièce jointe.
          Expéditeur : stage-notif@eclosion.be.
        </p>

        <label className="block">
          <span className="text-sm font-medium text-gray-700">Adresse du destinataire</span>
          <input
            type="email" value={adresse} onChange={(e) => setAdresse(e.target.value)}
            placeholder="famille@exemple.be"
            className="mt-1 w-full rounded-md border-gray-300 text-sm"
          />
        </label>

        <label className="mt-4 block">
          <span className="text-sm font-medium text-gray-700">Mot d'accompagnement</span>
          <textarea
            value={mot} onChange={(e) => setMot(e.target.value)} rows={3}
            placeholder="Facultatif — placé en tête du courriel."
            className="mt-1 w-full rounded-md border-gray-300 text-sm"
          />
        </label>

        {erreur && (
          <div className="mt-4 flex items-start gap-2 rounded-md border border-red-200 bg-red-50 p-3">
            <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0 text-red-600" />
            <p className="text-sm text-red-700">{erreur}</p>
          </div>
        )}

        <div className="mt-6 flex justify-end gap-3">
          <button onClick={onFerme} className="btn-outline">Annuler</button>
          <button
            onClick={() => void envoyer()}
            disabled={enCours || !adresse.trim()}
            className="btn-primary flex items-center gap-2 disabled:opacity-50"
          >
            {enCours ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />}
            Envoyer
          </button>
        </div>
      </div>
    </div>
  );
}

export default AdminGiftCodesPage;
